/**
 * Event handlers for AI Prescription page
 * Separated from main component for better organization and testability
 */

import { ConversationItem } from '../../utils/AIPrescription';
import {
  focusInputWithCursorAtEnd,
  getPrescriptionText,
  buildCombinedMedicationAndPharmacyPickMessage,
  mergePharmacyChipIntoInput,
  conversationScopedPickKey,
  picksForConversation,
} from '../../utils/AIPrescription/formUtils';
import type {
  MedicationSuggestionGroup,
  PharmacySuggestionGroup,
} from '../../utils/AIPrescription/prescriptionUtils';
import { scrollToLatest } from '../../utils/AIPrescription';
import { STORAGE_KEYS, TIMEOUTS } from '../../utils/AIPrescription/constants';
import { getTextWebSocketManager } from '../../Apis/AIPrescriptionApis/websocketManager';

export interface HandlersProps {
  conversations: ConversationItem[];
  setConversations: React.Dispatch<React.SetStateAction<ConversationItem[]>>;
  setPreferredPharmacy: (value: string) => void;
  setPharmacyIdFromAPI: (value: string | null) => void;
  formikRef: React.MutableRefObject<any>;
  resultsContainerRef: React.RefObject<HTMLDivElement>;
  latestConversationRef: React.RefObject<HTMLDivElement | null>;
  scrollTimeoutRef: React.MutableRefObject<NodeJS.Timeout | null>;
  medicationPicksByName: Record<string, string>;
  setMedicationPicksByName: React.Dispatch<
    React.SetStateAction<Record<string, string>>
  >;
  pharmacyPicksByName: Record<string, string>;
  setPharmacyPicksByName: React.Dispatch<
    React.SetStateAction<Record<string, string>>
  >;
  /** Submit composed chip text immediately (same path as pressing Send). */
  onAutoSend: (text: string) => void;
}

/**
 * Creates all event handlers for the AI Prescription component
 */
export const createHandlers = ({
  conversations,
  setConversations,
  setPreferredPharmacy,
  setPharmacyIdFromAPI,
  formikRef,
  resultsContainerRef,
  latestConversationRef,
  scrollTimeoutRef,
  medicationPicksByName,
  setMedicationPicksByName,
  pharmacyPicksByName,
  setPharmacyPicksByName,
  onAutoSend,
}: HandlersProps) => {
  const rememberPendingSelectedPharmacy = (
    pharmacyName: string,
    pharmacyId: string | undefined,
    medicationName: string,
    selectionText: string
  ) => {
    try {
      sessionStorage.setItem(
        STORAGE_KEYS.PENDING_SELECTED_PHARMACY,
        JSON.stringify({
          pharmacyId: pharmacyId || "",
          pharmacyName,
          medicationName,
          selectionText,
        })
      );
    } catch {
      // If storage is unavailable, the text fallback still lets orchestration proceed.
    }
  };

  /**
   * Stash the favourite-drug chip's full structured object (drugId, dispensibleDrugId,
   * personDrugId, pharmacyId, pharmacyName, strength, route, etc.) so createPayload() can
   * thread it into the outgoing WS message as `favouriteDrug` alongside the reconstructed
   * text below — letting the backend build a ready-to-confirm draft directly instead of
   * re-deriving the same data via extract_medication_info + get_search_pharmacies. Mirrors
   * rememberPendingSelectedPharmacy above.
   */
  const rememberPendingSelectedFavouriteDrug = (drug: any, selectionText: string) => {
    try {
      sessionStorage.setItem(
        STORAGE_KEYS.PENDING_SELECTED_FAVOURITE_DRUG,
        JSON.stringify({ drug, selectionText })
      );
    } catch {
      // If storage is unavailable, the text fallback still lets orchestration proceed
      // (just via the slower re-extraction path).
    }
  };

  /** Put text in the input and immediately submit (chip = one-click send). */
  const fillAndAutoSend = (text: string) => {
    const trimmed = (text || "").trim();
    if (!trimmed) return;
    focusInputWithCursorAtEnd(trimmed, formikRef);
    onAutoSend(trimmed);
    // Clear the input after submit so it doesn't linger for the next turn.
    if (formikRef?.current) {
      formikRef.current.setFieldValue("prescriptionText", "");
    }
  };

  const allGroupKeysPicked = (
    groups: { medication_name: string }[] | undefined,
    picks: Record<string, string>
  ): boolean => {
    if (!groups?.length) return false;
    return groups.every((g) => Boolean((picks[g.medication_name] || "").trim()));
  };

  /**
   * A turn can carry BOTH a pending medication-strength picker and a pending
   * pharmacy picker at once (e.g. "...update the strength to 100mg and use
   * brooklyn pharmacy" when the strength was ambiguous). Auto-send must wait
   * until every pending picker of BOTH kinds has a pick — not just every
   * pick within whichever single picker the user just clicked — otherwise
   * picking only the medication strength silently sends before the doctor
   * chose a pharmacy (and vice versa).
   */
  const readyToAutoSend = (
    medGroups: MedicationSuggestionGroup[] | undefined,
    medPicks: Record<string, string>,
    pharmGroups: PharmacySuggestionGroup[] | undefined,
    pharmPicks: Record<string, string>
  ): boolean => {
    const medReady = !medGroups?.length || allGroupKeysPicked(medGroups, medPicks);
    const pharmReady = !pharmGroups?.length || allGroupKeysPicked(pharmGroups, pharmPicks);
    return medReady && pharmReady;
  };

  /**
   * One catalog pick per medication group. Auto-sends only once every pending
   * medication group AND every pending pharmacy group (if any, from the same
   * turn) has a pick; otherwise the pick is staged in the input for review.
   */
  const handleSuggestionSelect = (
    suggestionText: string,
    medicationName?: string
  ) => {
    const lastConv =
      conversations.length > 0 ? conversations[conversations.length - 1] : null;
    const groups: MedicationSuggestionGroup[] | undefined =
      lastConv?.suggestionGroups;
    const pharmacyGroups = lastConv?.pharmacySuggestionGroups as
      | PharmacySuggestionGroup[]
      | undefined;

    // A flat, ungrouped suggestions list (Suggestions/index.tsx's non-grouped render
    // path, e.g. drug-alternative chips like "erythromycin 333 MG...") has no
    // medication key to pick against — Suggestions calls onSuggestionSelect(text)
    // with medicationName left undefined. Without this branch, medKey below resolves
    // to "", the chip's text never lands in medicationPicksByName, and
    // fillAndAutoSend gets called with an empty combined message that its own
    // `if (!trimmed) return` guard silently drops — the chip looks like it does
    // nothing. Send the chip text directly instead, same as every other one-click
    // chip (pharmacy lookup, PBM, favourite drug).
    if (!groups?.length) {
      fillAndAutoSend(suggestionText);
      return;
    }

    const medKey =
      (medicationName || "").trim() ||
      (groups?.length === 1 ? groups[0].medication_name : "");

    const compositeKey = medKey
      ? conversationScopedPickKey(lastConv?.id, medKey)
      : "";
    const nextPicks = compositeKey
      ? { ...medicationPicksByName, [compositeKey]: suggestionText }
      : medicationPicksByName;
    setMedicationPicksByName(nextPicks);

    const roundMedPicks = picksForConversation(nextPicks, lastConv?.id);
    const roundPharmPicks = picksForConversation(pharmacyPicksByName, lastConv?.id);

    const combined = buildCombinedMedicationAndPharmacyPickMessage(
      roundMedPicks,
      roundPharmPicks,
      groups,
      pharmacyGroups
    );

    if (readyToAutoSend(groups, roundMedPicks, pharmacyGroups, roundPharmPicks)) {
      fillAndAutoSend(combined);
    } else {
      focusInputWithCursorAtEnd(combined || suggestionText, formikRef);
    }
  };

  /**
   * Handles pharmacy lookup selection. Auto-sends only once every pending
   * pharmacy group AND every pending medication-strength group (if any, from
   * the same turn) has a pick; a bare (non-grouped) pharmacy lookup — not
   * part of a combined picker turn — still sends immediately as before.
   */
  const handlePharmacyLookupSelect = (
    pharmacyName: string,
    pharmacyId?: string,
    medicationName?: string
  ) => {
    const lastConv =
      conversations.length > 0 ? conversations[conversations.length - 1] : null;
    const pharmacyGroups = lastConv?.pharmacySuggestionGroups as
      | PharmacySuggestionGroup[]
      | undefined;
    const suggestionGroups = lastConv?.suggestionGroups;
    const medKey =
      (medicationName || "").trim() ||
      (pharmacyGroups?.length === 1 ? pharmacyGroups[0].medication_name : "");

    if (!medKey) {
      setPreferredPharmacy(pharmacyName);
      localStorage.setItem(STORAGE_KEYS.PREFERRED_PHARMACY, pharmacyName);

      if (pharmacyId) {
        setPharmacyIdFromAPI(pharmacyId);
        localStorage.setItem(STORAGE_KEYS.PHARMACY_ID, pharmacyId);
      }
    }

    if (pharmacyGroups && pharmacyGroups.length > 0 && medKey) {
      const nextPicks = {
        ...pharmacyPicksByName,
        [conversationScopedPickKey(lastConv?.id, medKey)]: pharmacyName,
      };
      setPharmacyPicksByName(nextPicks);
      const roundPharmPicks = picksForConversation(nextPicks, lastConv?.id);
      const roundMedPicks = picksForConversation(medicationPicksByName, lastConv?.id);
      const next = buildCombinedMedicationAndPharmacyPickMessage(
        roundMedPicks,
        roundPharmPicks,
        suggestionGroups,
        pharmacyGroups
      );
      rememberPendingSelectedPharmacy(pharmacyName, pharmacyId, medKey, next);
      if (readyToAutoSend(suggestionGroups, roundMedPicks, pharmacyGroups, roundPharmPicks)) {
        fillAndAutoSend(next);
      } else {
        focusInputWithCursorAtEnd(next, formikRef);
      }
      return;
    }

    setPharmacyPicksByName(
      medKey ? { [conversationScopedPickKey(lastConv?.id, medKey)]: pharmacyName } : {}
    );
    const cur = getPrescriptionText(formikRef);
    const next = mergePharmacyChipIntoInput(cur, pharmacyName, medKey);
    rememberPendingSelectedPharmacy(pharmacyName, pharmacyId, medKey, next);
    fillAndAutoSend(next);
  };

  /**
   * Handles a PBM / benefit-source chip selection and auto-sends the follow-up
   * message the orchestration PBM route recognizes.
   */
  const handlePbmSelect = (
    displayMemberUniqueId: string,
    _memberUniqueId?: string
  ) => {
    const label = (displayMemberUniqueId || "").trim();
    if (!label) return;
    fillAndAutoSend(`Use benefit source: ${label}`);
  };

  /**
   * Handles a favourite (saved) drug chip selection. Composes a fully-specified prescribe message
   * from the favourite's own fields (still shown as the visible chat line) and stashes the full
   * structured object so createPayload() can also send it as `favouriteDrug` — letting the
   * backend build a ready-to-confirm draft directly instead of re-deriving the same drug +
   * pharmacy via extract_medication_info + get_search_pharmacies.
   */
  const handleFavouriteDrugSelect = (drug: any) => {
    if (!drug || typeof drug !== "object") return;
    const description =
      (drug.description ||
        [
          drug.drugName,
          [drug.strength, drug.strengthUnit].filter(Boolean).join(" "),
          drug.route,
          drug.dosageForm,
        ]
          .filter(Boolean)
          .join(" "))?.toString().trim();
    if (!description) return;

    const parts: string[] = [`Prescribe ${description}`];
    if (drug.sig && String(drug.sig).trim()) {
      parts.push(`sig: ${String(drug.sig).trim()}`);
    }
    const qty = drug.quantity;
    if (qty !== undefined && qty !== null && String(qty).trim() !== "") {
      const qualifier = drug.quantityQualifier ? ` ${String(drug.quantityQualifier).trim()}` : "";
      parts.push(`quantity ${String(qty).trim()}${qualifier}`);
    }
    const refills = drug.refills;
    if (refills !== undefined && refills !== null && String(refills).trim() !== "") {
      parts.push(`${String(refills).trim()} refills`);
    }
    let message = parts.join(", ");
    if (drug.pharmacyName && String(drug.pharmacyName).trim()) {
      message += ` and use ${String(drug.pharmacyName).trim()} pharmacy`;
    }
    rememberPendingSelectedFavouriteDrug(drug, message);
    fillAndAutoSend(message);
  };

  /**
   * Handles feedback (thumbs up/down) for conversations
   */
  const handleFeedback = (
    conversationId: number,
    feedbackType: "up" | "down"
  ) => {
    // Persist to Langfuse (best-effort) only when toggling feedback ON, and only
    // if we have the turn's trace id. Done outside the state updater to avoid a
    // double-send under React StrictMode's double-invoked updaters.
    const conv = conversations.find((c) => c.id === conversationId);
    const togglingOn = !conv || conv.feedback !== feedbackType;
    if (togglingOn && conv?.traceId) {
      try {
        getTextWebSocketManager().sendFeedback(conv.traceId, feedbackType);
      } catch {
        // best-effort
      }
    }
    setConversations((prev) =>
      prev.map((conv) =>
        conv.id === conversationId
          ? {
              ...conv,
              feedback: conv.feedback === feedbackType ? null : feedbackType,
            }
          : conv
      )
    );
  };

  /**
   * Handles pharmacy selection from the pharmacy list
   * Updates prescriptions with selected pharmacy and creates new conversation
   */
  const handlePharmacySelection = (conversationId: number, pharmacyName: string) => {
    const conversation = conversations.find((conv) => conv.id === conversationId);
    if (!conversation) return;

    const timestamp = Date.now();
    const updatedPrescriptions = conversation.prescriptions.map((p, index) => ({
      ...p,
      id: timestamp + index + 1, // Generate new ID to force React re-render
      pharmacy: pharmacyName,
    }));

    // Update existing conversation and create new one showing completion
    setConversations((prev) => [
      ...prev.map((conv) =>
        conv.id === conversationId
          ? {
              ...conv,
              prescriptions: updatedPrescriptions,
              selectedPharmacy: pharmacyName,
              showPharmacyList: false,
            }
          : conv
      ),
      {
        id: timestamp + 1000,
        userQuery: `Select ${pharmacyName}`,
        aiResponse: "",
        isLoading: false,
        prescriptions: updatedPrescriptions,
        timestamp: new Date(),
        selectedPharmacy: pharmacyName,
        showPharmacyList: false,
      },
    ]);

    // Scroll to show latest conversation
    setTimeout(() => {
      scrollToLatest(resultsContainerRef, latestConversationRef, scrollTimeoutRef);
    }, TIMEOUTS.SCROLL_DELAY);
  };

  return {
    handleSuggestionSelect,
    handlePharmacyLookupSelect,
    handlePbmSelect,
    handleFavouriteDrugSelect,
    handleFeedback,
    handlePharmacySelection,
  };
};

